import { IApiBaseProps, IResultResponse } from '@common/api.models';
import { isValidApiBaseProps, isValidPaginatedApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { createUseFetchSearchFn } from '@common/search.services';
import { IItemResponse, IListResponse, IPagedResultsV1 } from '@gcl/react-core';
import { UseMutationOptions, useMutation, useQuery } from '@tanstack/react-query';

import { AxiosErrorResponse } from '@core/error.models';
import {
  ICreateProjectTransmittalDistributionRoleRequest,
  ICreateProjectTransmittalDistributionTeamRequest,
  ICreateProjectTransmittalDistributionUserRequest,
  ICreateProjectTransmittalRequest,
  IDeleteProjectTransmittalDistributionRequest,
  IDeleteProjectTransmittalRequest,
  IProjectTransmittal,
  IProjectTransmittalBase,
  IProjectTransmittalDetail,
  IProjectTransmittalDistribution,
  IProjectTransmittalDistributionRoleBase,
  IProjectTransmittalDistributionTeamBase,
  IProjectTransmittalDistributionUserBase,
  IProjectTransmittalOption,
  ITransmittalItem,
  ITransmittalSearchFilter,
  IUpdateProjectTransmittalRequest,
} from './transmittal.models';
import {
  createDraftProjectTransmittalIfNotExistAsync,
  deleteTransmittalAsync,
  deleteTransmittalDistributionRoleAsync,
  deleteTransmittalDistributionTeamAsync,
  deleteTransmittalDistributionUserAsync,
  endorseProjectTransmittalAsync,
  getLatestProjectTransmittalByMasterScheduleIdAsync,
  getProjectTransmittalByIdAsync,
  getProjectTransmittalDistributionsAsync,
  getProjectTransmittalSiteTransmittalForCheckerOptionsAsync,
  getProjectTransmittalSiteTransmittalForReviewerOptionsAsync,
  getTransmittalHistoriesAsync,
  getTransmittalHistoriesItemsAsync,
  IGetLatestTransmittalByMasterScheduleIdAsyncProps,
  IGetTransmittalHistoriesItemsProps,
  IGetTransmittalHistoriesProps,
  postTransmittalDistributionRoleAsync,
  postTransmittalDistributionTeamAsync,
  postTransmittalDistributionUserAsync,
  releaseForConstructionProjectTransmittalAsync,
  requestEndorseProjectTransmittalAsync,
  searchTransmittalAsync,
  updateProjectTransmittalAsync,
} from './transmittal.services';
import { IProjectTransmittalHistory } from './history/transmittalHistory.models';

export const useFetchTransmittal = createUseFetchSearchFn<ITransmittalSearchFilter, ITransmittalItem>({
  queryName: FETCH_QUERIES_NAME.TRANSMITTAL_SEARCH,
  searchAsyncFn: searchTransmittalAsync,
});

export const useFetchTransmittalHistories = (props: IGetTransmittalHistoriesProps) => {
  return useQuery<IPagedResultsV1<IProjectTransmittalHistory>, AxiosErrorResponse>(
    [
      props.projectId,
      props.projectMasterScheduleId,
      FETCH_QUERIES_NAME.TRANSMITTAL_HISTORIES,
      props.pageSize,
      props.pageNumber,
    ],
    async () => {
      const results = await getTransmittalHistoriesAsync(props);
      return results;
    },
    {
      enabled: isPositiveNumber(props.projectMasterScheduleId) && isValidPaginatedApiBaseProps(props),
    },
  );
};

export const useFetchTransmittalHistoriesItems = (props: IGetTransmittalHistoriesItemsProps) => {
  return useQuery(
    [
      props.projectId,
      props.projectMasterScheduleId,
      FETCH_QUERIES_NAME.TRANSMITTAL_HISTORIES_ITEMS,
      props.shouldShowRemovedFromUse,
    ],
    async () => {
      const results = await getTransmittalHistoriesItemsAsync(props);
      return results.items || [];
    },
    {
      enabled: isPositiveNumber(props.projectMasterScheduleId) && isValidApiBaseProps(props),
    },
  );
};

export const useCreateDraftProjectTransmittalIfNotExist = () => {
  return useMutation<IItemResponse<IProjectTransmittal>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.CREATE_DRAFT_PROJECT_TRANSMITTAL],
    async (request: ICreateProjectTransmittalRequest) => {
      const result = await createDraftProjectTransmittalIfNotExistAsync(request);
      return result;
    },
  );
};

export const useFetchTransmittalById = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery<IProjectTransmittalDetail | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.TRANSMITTAL],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getProjectTransmittalByIdAsync(id, props);
      return results.item;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};

export const useFetchLatestTransmittalByMasterScheduleId = (
  props: IGetLatestTransmittalByMasterScheduleIdAsyncProps,
  enabled = true,
) => {
  return useQuery<IProjectTransmittalBase | undefined, AxiosErrorResponse>(
    [props.projectId, props.masterScheduleId, props.transmittalType, FETCH_QUERIES_NAME.LATEST_TRANSMITTAL],
    async () => {
      const results = await getLatestProjectTransmittalByMasterScheduleIdAsync(props);
      return results.item;
    },
    {
      enabled:
        enabled && isPositiveNumber(props.masterScheduleId) && isValidApiBaseProps(props) && !!props.transmittalType,
    },
  );
};

export const useUpdateProjectTransmittal = () => {
  return useMutation<IItemResponse<IProjectTransmittal>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_TRANSMITTAL],
    async (updateRequest: IUpdateProjectTransmittalRequest) => {
      const result = await updateProjectTransmittalAsync(updateRequest);
      return result;
    },
  );
};

export const useDeleteProjectTransmittal = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.DELETE_PROJECT_TRANSMITTAL],
    async (request: IDeleteProjectTransmittalRequest) => {
      const result = await deleteTransmittalAsync(request);
      return result;
    },
  );
};

export const useEndorseProjectTransmittal = () => {
  return useMutation<IItemResponse<IProjectTransmittal>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.ENDORSE_PROJECT_TRANSMITTAL],
    async (updateRequest: IUpdateProjectTransmittalRequest) => {
      const result = await endorseProjectTransmittalAsync(updateRequest);
      return result;
    },
  );
};

export const useRequestEndorseProjectTransmittal = () => {
  return useMutation<IItemResponse<IProjectTransmittal>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.REQUEST_ENDORSE_PROJECT_TRANSMITTAL],
    async (updateRequest: IUpdateProjectTransmittalRequest) => {
      const result = await requestEndorseProjectTransmittalAsync(updateRequest);
      return result;
    },
  );
};

export const useReleaseForConstructionProjectTransmittal = () => {
  return useMutation<IItemResponse<IProjectTransmittal>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.RELEASE_FOR_CONSTRUCTION_PROJECT_TRANSMITTAL],
    async (updateRequest: IUpdateProjectTransmittalRequest) => {
      const result = await releaseForConstructionProjectTransmittalAsync(updateRequest);
      return result;
    },
  );
};

export const useCreateTransmittalDistributionTeam = (
  options?: UseMutationOptions<
    IListResponse<IProjectTransmittalDistributionTeamBase>,
    AxiosErrorResponse,
    any,
    unknown
  >,
) => {
  return useMutation<IListResponse<IProjectTransmittalDistributionTeamBase>, AxiosErrorResponse, any, unknown>({
    mutationKey: [MUTATION_KEYS.CREATE_TRANSMITTAL_DISTRIBUTION_TEAM],
    mutationFn: async (request: ICreateProjectTransmittalDistributionTeamRequest[]) => {
      const result = await postTransmittalDistributionTeamAsync(request);
      return result;
    },
    ...options,
  });
};

export const useCreateTransmittalDistributionRole = (
  options?: UseMutationOptions<
    IListResponse<IProjectTransmittalDistributionRoleBase>,
    AxiosErrorResponse,
    any,
    unknown
  >,
) => {
  return useMutation<IListResponse<IProjectTransmittalDistributionRoleBase>, AxiosErrorResponse, any, unknown>({
    mutationKey: [MUTATION_KEYS.CREATE_TRANSMITTAL_DISTRIBUTION_ROLE],
    mutationFn: async (request: ICreateProjectTransmittalDistributionRoleRequest[]) => {
      const result = await postTransmittalDistributionRoleAsync(request);
      return result;
    },
    ...options,
  });
};

export const useCreateTransmittalDistributionUser = (
  options?: UseMutationOptions<
    IListResponse<IProjectTransmittalDistributionUserBase>,
    AxiosErrorResponse,
    any,
    unknown
  >,
) => {
  return useMutation<IListResponse<IProjectTransmittalDistributionUserBase>, AxiosErrorResponse, any, unknown>({
    mutationKey: [MUTATION_KEYS.CREATE_TRANSMITTAL_DISTRIBUTION_USER],
    mutationFn: async (request: ICreateProjectTransmittalDistributionUserRequest[]) => {
      const result = await postTransmittalDistributionUserAsync(request);
      return result;
    },
    ...options,
  });
};

export const useDeleteTransmittalDistributionTeam = (
  options?: UseMutationOptions<IResultResponse, AxiosErrorResponse, any, unknown>,
) => {
  return useMutation<IResultResponse, AxiosErrorResponse, any, unknown>({
    mutationKey: [MUTATION_KEYS.DELETE_TRANSMITTAL_DISTRIBUTION_TEAM],
    mutationFn: async (request: IDeleteProjectTransmittalDistributionRequest) => {
      const result = await deleteTransmittalDistributionTeamAsync(request);
      return result;
    },
    ...options,
  });
};

export const useDeleteTransmittalDistributionRole = (
  options?: UseMutationOptions<IResultResponse, AxiosErrorResponse, any, unknown>,
) => {
  return useMutation<IResultResponse, AxiosErrorResponse, any, unknown>({
    mutationKey: [MUTATION_KEYS.DELETE_TRANSMITTAL_DISTRIBUTION_ROLE],
    mutationFn: async (request: IDeleteProjectTransmittalDistributionRequest) => {
      const result = await deleteTransmittalDistributionRoleAsync(request);
      return result;
    },
    ...options,
  });
};

export const useDeleteTransmittalDistributionUser = (
  options?: UseMutationOptions<IResultResponse, AxiosErrorResponse, any, unknown>,
) => {
  return useMutation<IResultResponse, AxiosErrorResponse, any, unknown>({
    mutationKey: [MUTATION_KEYS.DELETE_TRANSMITTAL_DISTRIBUTION_USER],
    mutationFn: async (request: IDeleteProjectTransmittalDistributionRequest) => {
      const result = await deleteTransmittalDistributionUserAsync(request);
      return result;
    },
    ...options,
  });
};

export const useFetchTransmittalDistribution = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery<IProjectTransmittalDistribution | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.TRANSMITTAL_DISTRIBUTION],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getProjectTransmittalDistributionsAsync(id, props);
      return results.item;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};

export const useFetchTransmittalSiteTransmittalForCheckerOptions = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IProjectTransmittalOption[] | undefined, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.TRANSMITTAL_SITE_TRANSMITTAL_FOR_CHECKER_OPTIONS],
    async () => {
      const results = await getProjectTransmittalSiteTransmittalForCheckerOptionsAsync(props);
      return results.items;
    },
    {
      enabled: enabled && isValidApiBaseProps(props),
    },
  );
};
export const useFetchTransmittalSiteTransmittalForReviewerOptions = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IProjectTransmittalOption[] | undefined, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.TRANSMITTAL_SITE_TRANSMITTAL_FOR_REVIEWER_OPTIONS],
    async () => {
      const results = await getProjectTransmittalSiteTransmittalForReviewerOptionsAsync(props);
      return results.items;
    },
    {
      enabled: enabled && isValidApiBaseProps(props),
    },
  );
};
